import React from "react";
import { ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { useTableData } from "../../../../hooks/useTableData";
import Flag from "../../../Flag";
import Image from "../../../Tags/Image";
import Pagination from "../../../Pagination";
import TableLoader from "../../../Loaders/TableLoader";
import NoData from "../../../NoData";

export default function DataTable({
  className = "",
  fetchFn,
  queryKey = "analytics_numbers_table",
  defaultSort = "",
  enabled = true,
}) {
  const {
    data,
    sort,
    handleSetSort,
    isLoading,
    isSuccess,
    isError,
    columnMetadata,
    paginationInfo,
    getPageNumber,
  } = useTableData({
    queryKey,
    fetchFn,
    defaultSort,
    enabled,
  });

  const SortIcon = ({ column }) => {
    if (sort === "" || (sort !== column && sort !== `-${column}`)) {
      return <Image src="/images/icons/dual-arrow.svg" width="7" height="7" className="inline-block ml-1" />;
    }
    return sort === column ? (
      <ArrowDownIcon width={12} className="inline-block ml-1" />
    ) : (
      <ArrowUpIcon width={12} className="inline-block ml-1" />
    );
  };

  const getTrendFromChangePercent = (changePercent) => {
    if (changePercent > 0) return "up";
    if (changePercent < 0) return "down";
    return "neutral";
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") return "↗";
    if (trend === "down") return "↘";
    return "";
  };

  const getTrendBadgeClasses = (trend) => {
    // up: bg-[#ECFDF3] border-[#ABEFC6] text-[#067647]
    // down: bg-[#FEF3F2] border-[#FECDCA] text-[#B42318]
    // neutral: bg-gray-50 border-gray-200 text-gray-600
    if (trend === "up") {
      return "bg-[#ECFDF3] dark:bg-[#053321] border border-[#ABEFC6] dark:border-[#085D3A] text-[#067647] dark:text-[#75E0A7]";
    } else if (trend === "down") {
      return "bg-[#FEF3F2] dark:bg-[#55160C] border border-[#FECDCA] dark:border-[#912018] text-[#B42318] dark:text-[#FDA29B]";
    } else {
      return "bg-gray-50 border border-gray-200 text-gray-600";
    }
  };

  if (isLoading && !isSuccess) {
    return <TableLoader />;
  }

  if (isSuccess && (!data || data.length === 0)) {
    return (
      <NoData
        heading="No phone numbers found"
        description="No phone numbers data available for the selected criteria"
      />
    );
  }

  return (
    <div
      className={`bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg overflow-hidden ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-b-[#E9EAEB] dark:border-b-[#22262F]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                {columnMetadata?.phone_number?.sortable ? (
                  <button
                    className="flex items-center gap-1 hover:text-gray-700"
                    onClick={() => handleSetSort("phone_number")}
                  >
                    {columnMetadata?.phone_number?.display_name || "Number"}
                    <SortIcon column="phone_number" />
                  </button>
                ) : (
                  <span>{columnMetadata?.phone_number?.display_name || "Number"}</span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                {columnMetadata?.outbound_calls?.sortable ? (
                  <button
                    className="flex items-center gap-1 hover:text-gray-700"
                    onClick={() => handleSetSort("outbound_calls")}
                  >
                    {columnMetadata?.outbound_calls?.display_name || "Outbound"}
                    <SortIcon column="outbound_calls" />
                  </button>
                ) : (
                  <span>{columnMetadata?.outbound_calls?.display_name || "Outbound"}</span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                {columnMetadata?.inbound_calls?.sortable ? (
                  <button
                    className="flex items-center gap-1 hover:text-gray-700"
                    onClick={() => handleSetSort("inbound_calls")}
                  >
                    {columnMetadata?.inbound_calls?.display_name || "Inbound"}
                    <SortIcon column="inbound_calls" />
                  </button>
                ) : (
                  <span>{columnMetadata?.inbound_calls?.display_name || "Inbound"}</span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                {columnMetadata?.missed_calls?.sortable ? (
                  <button
                    className="flex items-center gap-1 hover:text-gray-700"
                    onClick={() => handleSetSort("missed_calls")}
                  >
                    {columnMetadata?.missed_calls?.display_name || "Missed"}
                    <SortIcon column="missed_calls" />
                  </button>
                ) : (
                  <span>{columnMetadata?.missed_calls?.display_name || "Missed"}</span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                {columnMetadata?.messages_sent?.sortable ? (
                  <button
                    className="flex items-center gap-1 hover:text-gray-700"
                    onClick={() => handleSetSort("messages_sent")}
                  >
                    {columnMetadata?.messages_sent?.display_name || "Messages Sent"}
                    <SortIcon column="messages_sent" />
                  </button>
                ) : (
                  <span>{columnMetadata?.messages_sent?.display_name || "Messages Sent"}</span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#717680] uppercase tracking-wider">
                {columnMetadata?.connect_rate?.sortable ? (
                  <button
                    className="flex items-center gap-1 hover:text-gray-700"
                    onClick={() => handleSetSort("connect_rate")}
                  >
                    {columnMetadata?.connect_rate?.display_name || "Connect Rate"}
                    <SortIcon column="connect_rate" />
                  </button>
                ) : (
                  <span>{columnMetadata?.connect_rate?.display_name || "Connect Rate"}</span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-[#0C0E12] divide-y divide-gray-200 dark:divide-gray-200">
            {data.map((row, index) => {
              const trend = getTrendFromChangePercent(row.connect_rate_change_percent);

              return (
                <tr key={`${row.phone_number}-${index}`} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-[40px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border mr-2">
                        <Flag code={row.country_code.toLowerCase()} squared />
                      </span>
                      <div className="">
                        <div className="font-semibold text-[#101828] dark:text-white text-sm">
                          {row.phone_number}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862]">{row.outbound_calls}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862]">{row.inbound_calls}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862]">{row.missed_calls}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[#535862]">{row.messages_sent}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{row.connect_rate}%</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTrendBadgeClasses(trend)}`}
                      >
                        {trend === "neutral"
                          ? "No change"
                          : `${getTrendIcon(trend)} ${Math.abs(row.connect_rate_change_percent)}%`}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginationInfo && paginationInfo.total > 0 && (
        <div className="flex items-center justify-between rounded-[--radius] py-3 px-6">
          <div className="flex-1">
            <Pagination data={paginationInfo} getPageNumber={getPageNumber} />
          </div>
        </div>
      )}
    </div>
  );
}
